import { useTranslation } from "../../context/TranslationContext";
import Button from "../ds/Button";
import Icon from "../ds/Icon";

/*
  Figma "Balance summary" (DS · კომპონენტები): the mint wallet card beside On hold. Big
  amounts are never cut: the wallet grows and On hold gives up the room, with its progress
  bar kept to at least 140. Too narrow for both and they stack, mint on top.
*/

const HOUR = 60 * 60 * 1000;
const money = (n) => `${(Number(n) || 0).toFixed(2)} ₾`;

const CARD_LABEL = "text-caps-label uppercase";

/** How long until a release: hours up to three days, days after that. */
const timeUntil = (ms, t) => {
  const hours = Math.ceil(ms / HOUR);
  if (hours <= 1) return t("balancePage.inAnHour");
  if (hours <= 72) return t("balancePage.inHours", { count: hours });
  return t("balancePage.inDays", { count: Math.ceil(hours / 24) });
};

const holdLength = (days, t) =>
  days <= 3 ? t("balancePage.forHours", { count: days * 24 }) : t("balancePage.forDays", { count: days });

/** The next release: how far through its hold it is, and when it lands. */
const nextRelease = (amount, holds, t) => {
  const next = holds[0];
  if (!next) return null;
  const now = Date.now();
  const start = new Date(next.heldSince).getTime();
  const end = new Date(next.releaseAt).getTime();
  const progress = end > start ? Math.min(1, Math.max(0, (now - start) / (end - start))) : 1;
  const time = timeUntil(end - now, t);
  const isAll = next.amount >= amount - 0.005;
  return {
    progress,
    note: isAll
      ? t("balancePage.releasedTime", { time })
      : t("balancePage.partReleasedTime", { amount: money(next.amount), time }),
  };
};

const WalletButton = ({ disabled = false, ...rest }) => (
  <Button
    variant="gray"
    size="m"
    fullWidth
    disabled={disabled}
    className={`!bg-accent-on !text-accent-default ${disabled ? "opacity-40" : ""}`}
    {...rest}
  />
);

const OnHoldCard = ({ amount, holds, holdDays }) => {
  const { t } = useTranslation();
  const card =
    "flex min-w-0 grow-[9999] basis-[420px] items-center gap-space-32 rounded-radius-lg bg-bg-raised px-space-24 py-space-20 max-[899px]:basis-auto max-[899px]:flex-col max-[899px]:items-stretch max-[899px]:gap-space-12 max-[899px]:p-space-20";
  const label = <p className={`${CARD_LABEL} text-label-tertiary`}>{t("balancePage.onHold")}</p>;

  if (amount <= 0) {
    return (
      <div className={card}>
        <div className="flex shrink-0 flex-col gap-space-4">
          {label}
          <p className="text-title-3 text-label-secondary">{t("balancePage.nothingOnHold")}</p>
        </div>
        <p className="min-w-0 flex-1 text-footnote text-label-tertiary">
          {holdDays === 0
            ? t("balancePage.noHoldNote")
            : t("balancePage.holdNote", { time: holdLength(holdDays ?? 3, t) })}
        </p>
      </div>
    );
  }

  const release = nextRelease(amount, holds, t);
  return (
    <div className={card}>
      <div className="flex shrink-0 flex-col gap-space-4">
        {label}
        <p className="text-title-3 text-label-primary">{money(amount)}</p>
      </div>
      <div className="flex min-w-[140px] flex-1 flex-col gap-space-12">
        {release && (
          <div className="h-1.5 w-full overflow-hidden rounded-full bg-fill-secondary">
            <div className="h-full rounded-full bg-accent-default" style={{ width: `${release.progress * 100}%` }} />
          </div>
        )}
        <p className="text-footnote text-label-secondary">{release ? release.note : t("balancePage.onHoldHint")}</p>
      </div>
    </div>
  );
};

const BalanceSummary = ({ available, onHold, holds = [], holdDays, minWithdrawal, onWithdraw, onTopUp }) => {
  const { t } = useTranslation();
  const canWithdraw = available > 0 && available >= minWithdrawal;
  const minimumHint = t("balancePage.minWithdrawalHint", { amount: money(minWithdrawal) });

  return (
    <section className="flex flex-wrap gap-space-12 max-[899px]:flex-col" aria-label={t("balancePage.title")}>
      <div className="flex grow flex-wrap items-center justify-between gap-x-space-32 gap-y-space-16 rounded-radius-lg bg-accent-default px-space-24 py-space-20 text-accent-on max-[899px]:gap-x-space-24 max-[899px]:px-space-20">
        <div className="flex flex-col gap-space-4">
          <p className={CARD_LABEL}>{t("balancePage.availableBalance")}</p>
          <p className="whitespace-nowrap text-title-1">{money(available)}</p>
          {/* A disabled button reads as broken unless it says why. */}
          <p className="text-footnote">{available > 0 && !canWithdraw ? minimumHint : t("balancePage.readyToWithdraw")}</p>
        </div>
        <div className="flex w-[148px] shrink-0 flex-col gap-space-8">
          <WalletButton
            icon={<Icon name="wallet" size={20} />}
            disabled={!canWithdraw}
            onClick={onWithdraw}
            title={canWithdraw ? undefined : minimumHint}
          >
            {t("balancePage.withdraw")}
          </WalletButton>
          <WalletButton icon={<Icon name="plus" size={20} />} onClick={onTopUp}>
            {t("balancePage.topUp")}
          </WalletButton>
        </div>
      </div>
      <OnHoldCard amount={onHold} holds={holds} holdDays={holdDays} />
    </section>
  );
};

export default BalanceSummary;
